import React, { Fragment, ReactElement, useState } from 'react';
import { Action, Dispatch } from 'redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Programme } from '../../../../../types/entity.types';

import { formatProgrammeDates, getClassNames, isProgrammeIncomplete } from '../../../../../utils/util';

import { setCollections } from '../../../../../slices/programme/plan/plan.slice';
import { setAccountabilityTemplate } from '../../../../../slices/programme/accountability/accountability.slice';
import { setSupportTemplate } from '../../../../../slices/programme/support/support.slice';
import { setTribeTemplate } from '../../../../../slices/programme/tribe/tribe.slice';

import './programme-menu-section.scss';

type Props = {
  heading: string;
  programmes: Programme[];
  onNavigate?: (value: string) => void;
};

const ProgrammeMenuSection = ({ heading, programmes = [], onNavigate }: Props): ReactElement => {
  const [open, setOpen] = useState<boolean>(true);

  const dispatch: Dispatch<Action> = useDispatch();

  const navigate: NavigateFunction = useNavigate();

  const { programmeExternalReference }: Params = useParams();

  if (programmes.length === 0) return <Fragment />;

  const handleProgrammeClick = (programme: Programme, selected: boolean): void => {
    if (selected) return;

    dispatch(setCollections([]));
    dispatch(setAccountabilityTemplate(undefined));
    dispatch(setSupportTemplate(undefined));
    dispatch(setTribeTemplate(undefined));

    const { externalReference, status }: Programme = programme;
    const url: string = `/programmes/${status}/${externalReference}/general/members`;

    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }
  };

  const renderMenuItems = (): ReactElement[] => {
    const copyOfProgrammes: Programme[] = structuredClone(programmes);

    copyOfProgrammes.sort((programmeOne: Programme, programmeTwo: Programme): number => {
      const dateOne: Date = new Date(programmeOne.fromDate);
      const dateTwo: Date = new Date(programmeTwo.fromDate);

      if (dateOne > dateTwo) return 1;
      if (dateOne < dateTwo) return -1;
      return 0;
    });

    return copyOfProgrammes.map((programme: Programme, index: number): ReactElement => {
      const selected: boolean = programme.externalReference === programmeExternalReference;

      return (
        <div
          className="programme-menu-section__item"
          key={index}
          onClick={(): void => handleProgrammeClick(programme, selected)}
          data-testid="menu-item"
        >
          <div className={getClassNames('programme-menu-section__item__name', { selected })}>
            <p>{programme.name}</p>
            {isProgrammeIncomplete(programme) && (
              <div className="programme-menu-section__item__icon" data-testid="incomplete-icon">
                <p>!</p>
              </div>
            )}
          </div>
          <p className={getClassNames('programme-menu-section__item__date', { selected })}>
            {formatProgrammeDates(programme.fromDate, programme.toDate)}
          </p>
        </div>
      );
    });
  };

  return (
    <div className="programme-menu-section">
      <div className="programme-menu-section__heading">
        <div className="programme-menu-section__heading__text">{heading}</div>
        <div
          className="programme-menu-section__heading__button"
          onClick={(): void => setOpen((prevState: boolean): boolean => !prevState)}
        >
          {!open && <FontAwesomeIcon icon={faPlus} title="plus icon" />}
          {open && <FontAwesomeIcon icon={faMinus} title="minus icon" />}
        </div>
      </div>
      <div className={getClassNames('programme-menu-section__items', { open })}>{renderMenuItems()}</div>
    </div>
  );
};

export default ProgrammeMenuSection;
